import React, { useState } from "react";
import { Button, Group, Loader, Text } from "@mantine/core";
import { FaSave } from "react-icons/fa";
import { usePostRoutingGraphSaveMutation } from "../../../openapi";
import HSpace from "../../HSpace";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearUnsavedRoutingItems,
  removeUnsavedRoutingItem,
  selectUnsavedRoutingItems,
} from "./unsavedRoutingSlice";

const UnsavedPipesBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const unsavedPipes = useAppSelector(selectUnsavedRoutingItems);
  const [saveRoutingGraph] = usePostRoutingGraphSaveMutation();
  const [isSaving, setIsSaving] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const count = unsavedPipes.length;
  const hasPipes = count > 0;

  const handlePublish = async () => {
    if (!hasPipes || isSaving) return;

    setIsSaving(true);
    setErrorText(null);

    try {
      for (const item of unsavedPipes) {
        await saveRoutingGraph({ body: item.routingGraph }).unwrap();
        dispatch(removeUnsavedRoutingItem(item.localId));
      }
    } catch (error) {
      console.warn("Failed to save routing graphs", error);
      setErrorText("Kunne ikke lagre alle rør. Prøv igjen.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Group
      w="100%"
      px="md"
      py={8}
      justify="space-between"
      align="center"
      gap="sm"
      style={{
        backgroundColor: "var(--mantine-color-blue-6)",
        color: "var(--mantine-color-white)",
      }}
    >
      <Group gap="xs" align="center">
        {!isSaving && (
          <Group gap="xs">
            <Button
              size="md"
              variant="white"
              color="green"
              disabled={!hasPipes}
              onClick={handlePublish}
            >
              <FaSave />
              <HSpace />
              Publiser rør
            </Button>

            <Button
              size="md"
              variant="outline"
              color="white"
              disabled={!hasPipes}
              onClick={() => dispatch(clearUnsavedRoutingItems())}
            >
              Avbryt
            </Button>
          </Group>
        )}

        {isSaving && <Loader size="sm" color="white" />}

        <Text size="sm" c="white">
          {isSaving
            ? "Lagrer rør …"
            : hasPipes
              ? `Klar til å publisere ${count} rørtegning${count === 1 ? "" : "er"}.`
              : "Ingen nye rør å publisere."}
        </Text>

        {errorText && (
          <Text size="sm" c="red.1">
            {errorText}
          </Text>
        )}
      </Group>
    </Group>
  );
};

export default UnsavedPipesBar;
